import { useState, useEffect } from 'react'
import { motion } from 'motion/react'
import { Link } from '@tanstack/react-router'
import {
  Copy, CheckCircle2, Play, X, RotateCw, ChevronDown,
  ShieldCheck, FileText, FlaskConical, Sparkles, DollarSign, Search,
  Scale, Zap, Box, Briefcase, Send, Database,
  Network, Recycle, GitMerge, Package, Users,
} from 'lucide-react'
import { Button, Badge } from '@/components/ui'
import { cn, formatRelativeDate } from '@/lib/utils'
import { useUpdateImprovement, type Improvement, type ImprovementCategory, type ImprovementImpact, type ImprovementEffort } from '@/hooks/useImprovements'
import { toast } from 'sonner'

const CATEGORY_ICON: Record<ImprovementCategory, React.ComponentType<{ size?: number }>> = {
  security: ShieldCheck, docs: FileText, tests: FlaskConical, branding: Sparkles,
  monetization: DollarSign, seo: Search, compliance: Scale, performance: Zap,
  product: Box, legal: Scale, distribution: Send, data: Database,
}

const IMPACT_TONE: Record<ImprovementImpact, string> = {
  high: 'bg-success/15 text-success border-success/40',
  medium: 'bg-warning/15 text-warning border-warning/40',
  low: 'bg-cream/10 text-cream/65 border-cream/25',
}

const EFFORT_LABEL: Record<ImprovementEffort, { label: string; color: string }> = {
  quick:  { label: '⚡ Quick',  color: 'text-success' },
  medium: { label: '◐ Medium', color: 'text-warning' },
  deep:   { label: '◔ Deep',    color: 'text-bronze' },
}

export function ImprovementCard({ item, showApp = false }: { item: Improvement; showApp?: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const [editingNotes, setEditingNotes] = useState(false)
  const [notes, setNotes] = useState(item.notes ?? '')
  const update = useUpdateImprovement()

  // Keep the local notes draft in sync with live updates to the underlying
  // item, unless the user is actively editing (don't clobber in-flight edits).
  useEffect(() => {
    if (!editingNotes) setNotes(item.notes ?? '')
  }, [item.notes, editingNotes])

  const Icon = CATEGORY_ICON[item.category] ?? Briefcase

  function copyPrompt() {
    navigator.clipboard.writeText(item.prompt)
    toast.success(`Prompt copied · paste into Claude/Gemini/Cursor`)
  }

  function setStatus(status: Improvement['status']) {
    update.mutate({ itemId: item.id, status })
  }

  function saveNotes() {
    update.mutate({ itemId: item.id, notes })
    setEditingNotes(false)
  }

  function dismiss() {
    const reason = prompt('Why dismiss this suggestion? (optional, helps the next audit avoid it)')
    // prompt() returns null when the user cancels — don't dismiss in that case.
    if (reason === null) return
    update.mutate({ itemId: item.id, status: 'dismissed', dismissed_reason: reason })
  }

  const isDone = item.status === 'done'
  const isDismissed = item.status === 'dismissed'
  const isInProgress = item.status === 'in-progress'

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'glass rounded-lg p-4 transition-all',
        isDone && 'opacity-60',
        isDismissed && 'opacity-40',
        isInProgress && 'border-gold/40',
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          'shrink-0 mt-1 size-8 rounded-md flex items-center justify-center',
          'bg-bronze/10 text-bronze',
        )}>
          <Icon size={14} />
        </div>

        <div className="flex-1 min-w-0">
          <button onClick={() => setExpanded(!expanded)} className="block w-full text-left">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                {showApp && (
                  <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-bronze mb-1">
                    {item.app_entity} · {item.app_name}
                  </div>
                )}
                <h4 className={cn(
                  'font-heading text-base text-cream leading-snug',
                  isDone && 'line-through',
                )}>
                  {item.title}
                </h4>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <span className={cn('px-2 py-0.5 rounded border font-mono text-[10px] uppercase tracking-[0.12em]', IMPACT_TONE[item.impact])}>
                  {item.impact} impact
                </span>
                <span className={cn('font-mono text-[10px] uppercase tracking-[0.1em]', EFFORT_LABEL[item.effort].color)}>
                  {EFFORT_LABEL[item.effort].label}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge tone="bronze" className="!py-0">{item.category}</Badge>
              <span className="font-mono text-[10px] text-gold-soft">{item.expected_uplift}</span>
              <span className="font-mono text-[10px] text-cream/40">·  iteration {item.generated_iteration}</span>
              <span className="font-mono text-[10px] text-cream/40">·  {formatRelativeDate(item.generated_at)}</span>
              <ChevronDown size={12} className={cn('text-cream/40 ml-auto transition-transform', expanded && 'rotate-180')} />
            </div>
          </button>

          {expanded && (
            <div className="mt-4 pt-4 border-t border-cream/10 space-y-3">
              <div>
                <div className="eyebrow mb-1.5">Rationale</div>
                <p className="font-body text-sm text-cream/80 leading-relaxed">{item.rationale}</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="eyebrow">Execution prompt</span>
                  <Button size="sm" variant="quiet" onClick={copyPrompt}>
                    <Copy size={11} /> Copy
                  </Button>
                </div>
                <pre className="font-mono text-[11px] text-cream/85 whitespace-pre-wrap leading-relaxed bg-navy/60 border border-bronze/20 rounded p-3 max-h-64 overflow-auto">
                  {item.prompt}
                </pre>
                <div className="font-mono text-[9px] text-cream/40 mt-1">
                  Paste into Claude Code, Gemini, Cursor, or any LLM CLI to execute.
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="eyebrow">Notes / progress</span>
                  {!editingNotes && (
                    <button onClick={() => setEditingNotes(true)} className="font-mono text-[10px] text-bronze hover:text-gold-soft uppercase tracking-[0.12em]">
                      Edit
                    </button>
                  )}
                </div>
                {editingNotes ? (
                  <div className="space-y-2">
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="What did you do? Where did you push it? Anything blocking?"
                      className="w-full bg-navy/50 border border-cream/15 rounded px-3 py-2 text-sm text-cream font-body min-h-20 focus:outline-none focus:border-gold-soft"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveNotes} loading={update.isPending}>Save</Button>
                      <Button size="sm" variant="quiet" onClick={() => { setNotes(item.notes ?? ''); setEditingNotes(false) }}>Cancel</Button>
                    </div>
                  </div>
                ) : item.notes ? (
                  <p className="font-body text-sm text-cream/75 italic">{item.notes}</p>
                ) : (
                  <p className="font-body text-sm text-cream/35 italic">No notes yet.</p>
                )}
              </div>

              {item.cross_app_opportunities && item.cross_app_opportunities.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 eyebrow mb-2 !text-bronze">
                    <Network size={11} /> Portfolio synergies
                  </div>
                  <div className="space-y-2">
                    {item.cross_app_opportunities.map((c, i) => {
                      const Icon = c.type === 'reuse' ? Recycle : c.type === 'merge' ? GitMerge : c.type === 'bundle' ? Package : Users
                      const tone = c.type === 'reuse' ? 'success' : c.type === 'merge' ? 'gold' : c.type === 'bundle' ? 'info' : 'bronze'
                      const toneClasses: Record<string, string> = {
                        success: 'border-success/30 bg-success/5',
                        gold: 'border-gold/30 bg-gold/5',
                        info: 'border-info/30 bg-info/5',
                        bronze: 'border-bronze/30 bg-bronze/5',
                      }
                      return (
                        <div key={i} className={cn('rounded border p-3', toneClasses[tone])}>
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <Icon size={12} className="text-bronze" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-cream font-medium">{c.type.replace('_', ' ')}</span>
                            <span className="font-mono text-[10px] text-cream/40">→</span>
                            <Link to="/apps/$slug" params={{ slug: c.other_app_slug }} className="font-mono text-[10px] text-gold-soft hover:underline">
                              {c.other_app_name}
                            </Link>
                          </div>
                          <p className="font-body text-xs text-cream/80 leading-relaxed">{c.rationale}</p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {item.dismissed_reason && (
                <div>
                  <div className="eyebrow mb-1">Dismissed because</div>
                  <p className="font-body text-sm text-danger/85 italic">{item.dismissed_reason}</p>
                </div>
              )}

              <div className="flex items-center gap-2 pt-3 border-t border-cream/8 flex-wrap">
                {item.status === 'suggested' && (
                  <>
                    <Button size="sm" variant="gold-outline" onClick={() => setStatus('in-progress')}>
                      <Play size={11} /> Start
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setStatus('done')}>
                      <CheckCircle2 size={11} /> Mark done
                    </Button>
                    <Button size="sm" variant="quiet" onClick={dismiss}>
                      <X size={11} /> Dismiss
                    </Button>
                  </>
                )}
                {item.status === 'in-progress' && (
                  <>
                    <Button size="sm" onClick={() => setStatus('done')}>
                      <CheckCircle2 size={11} /> Mark done
                    </Button>
                    <Button size="sm" variant="quiet" onClick={() => setStatus('suggested')}>
                      <RotateCw size={11} /> Pause
                    </Button>
                    <Button size="sm" variant="quiet" onClick={dismiss}>Dismiss</Button>
                  </>
                )}
                {item.status === 'done' && (
                  <Button size="sm" variant="quiet" onClick={() => setStatus('suggested')}>
                    <RotateCw size={11} /> Reopen
                  </Button>
                )}
                {item.status === 'dismissed' && (
                  <Button size="sm" variant="quiet" onClick={() => setStatus('suggested')}>
                    <RotateCw size={11} /> Restore
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}
